/**
 * Formatting the dates a campaign and a web carry (ORB-849).
 *
 * ⚑⚑ Every one of these is a CALENDAR DATE, and none of them may go through `new Date(value)` on
 * its own. A campaign's `StartDate` is a C# `DateTime?` assigned straight through by `CampaignMapper`,
 * and this screen POSTs the bare `2026-09-12` an `<input type=date>` produces — which
 * System.Text.Json parses as `DateTimeKind.Unspecified` and serialises back **without a `Z`**. So the
 * same field arrives Z-suffixed from one write and zoneless from the next, and `new Date` reads the
 * two in different zones: UTC for the first, LOCAL for the second. Either a UTC render moves the
 * zoneless one forward east of UTC, or a local render moves the Z one back west of it.
 *
 * `calendarParts` sidesteps the whole question by taking the `YYYY-MM-DD` off the front of the
 * string, which all three wire forms (`2026-09-12`, `…T00:00:00`, `…T00:00:00Z`) agree on. That is
 * the same idiom `campaigns/CampaignModal.tsx` already uses in the app — `StartDate.split('T')[0]`.
 *
 * Both halves of `reference_api_zoneless_utc_dates` are live here at once, which is why neither a
 * UTC nor a local render is correct on its own.
 *
 * A sign-off is a real instant, and an online event's times are its own wall clock. Neither is a
 * calendar date, and the helpers for both are at the bottom, each saying which zone it renders in.
 */

import { eventZoneInstant, namedAbbreviation, shortOffset } from '@/utils/timeZones'

/** The `YYYY-MM-DD` the wire agrees on, whatever tail the serialiser put after it. Null if absent. */
const CALENDAR_DATE = /^(\d{4})-(\d{2})-(\d{2})/

function calendarParts(value: string | null | undefined): { year: number; month: number; day: number } | null {
  if (!value) return null
  const match = CALENDAR_DATE.exec(value.trim())
  if (!match) return null
  const [, year, month, day] = match
  return { year: Number(year), month: Number(month), day: Number(day) }
}

/**
 * The same calendar date as a UTC instant, for formatting and for arithmetic.
 *
 * Built from the parts rather than parsed, so nothing in it depends on the reader's zone.
 */
function calendarUtc(value: string | null | undefined): Date | null {
  const parts = calendarParts(value)
  if (!parts) return null
  const parsed = new Date(Date.UTC(parts.year, parts.month - 1, parts.day))
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

/** A `DateTime?` off the wire, as the `<input type=date>` value it belongs in. */
export function dateInputValue(value: string | null | undefined): string {
  const parts = calendarParts(value)
  if (!parts) return ''
  const pad = (part: number) => String(part).padStart(2, '0')
  return `${parts.year}-${pad(parts.month)}-${pad(parts.day)}`
}

/** One calendar date, as `12 Sept`. Null for anything unparseable, so a caller can say "Not set". */
export function calendarDay(value: string | null | undefined): string | null {
  const parsed = calendarUtc(value)
  if (!parsed) return null
  return parsed.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' })
}

/** A campaign's span, as `12 Sept – 30 Oct`, or one open end, or null when it has neither. */
export function dateRange(start: string | null | undefined, end: string | null | undefined): string | null {
  const from = calendarDay(start)
  const to = calendarDay(end)
  if (from && to) return `${from} – ${to}`
  if (from) return `From ${from}`
  if (to) return `Until ${to}`
  return null
}

/**
 * How far through its own dates a campaign is, for the line under its name.
 *
 * Null unless it has both ends and they are the right way round — a progress bar against a span
 * that does not exist is a bar reporting a number nobody supplied.
 */
export function campaignProgress(
  start: string | null | undefined,
  end: string | null | undefined,
): { percent: number; label: string } | null {
  const fromDate = calendarUtc(start)
  const toDate = calendarUtc(end)
  if (!fromDate || !toDate) return null
  const from = fromDate.getTime()
  const to = toDate.getTime()
  if (to <= from) return null
  const days = Math.round((to - from) / 86_400_000)
  const done = Math.round((Date.now() - from) / 86_400_000)
  // `1 days` is the kind of thing a reader notices and nothing else does, so every count here agrees
  // with itself the way the audience and speaker lines already do.
  const plural = (count: number) => (count === 1 ? 'day' : 'days')
  if (done < 0) return { percent: 0, label: `Starts in ${Math.abs(done)} ${plural(Math.abs(done))}` }
  if (done > days) return { percent: 100, label: `Ended, ${days} ${plural(days)} long` }
  return { percent: Math.round((done / days) * 100), label: `Day ${done} of ${days}` }
}

/** One calendar date with its year, as `2 Sept 2026`. Null for anything unparseable. */
export function calendarDate(value: string | null | undefined): string | null {
  const parsed = calendarUtc(value)
  if (!parsed) return null
  return parsed.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' })
}

/**
 * Whether a calendar date is still ahead of the reader's own today.
 *
 * ⚑ Today is the reader's, the date is the wire's `YYYY-MM-DD`. A web launching on the 30th launches
 * on the 30th wherever it is read, so neither side is moved through a zone.
 */
export function isCalendarAhead(value: string | null | undefined, now: Date = new Date()): boolean {
  return (calendarDaysUntil(value, now) ?? 0) > 0
}

/** A web's launch, as `Launches 30 Sept 2026` or `Launched 2 Sept 2026`. Null when it has none. */
export function launchLine(value: string | null | undefined, now: Date = new Date()): string | null {
  const date = calendarDate(value)
  if (!date) return null
  return `${isCalendarAhead(value, now) ? 'Launches' : 'Launched'} ${date}`
}

/**
 * A real instant, parsed as UTC when the wire left the zone off.
 *
 * ⚑ `reference_api_zoneless_utc_dates`: some UTC `DateTime`s serialise without a `Z`, and `new Date`
 * reads those as the reader's LOCAL time. Every instant here is UTC on the server, so a missing
 * designator means UTC, not local.
 */
function instant(value: string | null | undefined): Date | null {
  if (!value) return null
  const trimmed = value.trim()
  const zoned = /[zZ]$|[+-]\d{2}:?\d{2}$/.test(trimmed) || !trimmed.includes('T') ? trimmed : `${trimmed}Z`
  const parsed = new Date(zoned)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

/** The day an instant fell on for the reader, as `18 Sept` — when a web was signed off, for instance. */
export function instantDay(value: string | null | undefined): string | null {
  const parsed = instant(value)
  if (!parsed) return null
  return parsed.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
}

/**
 * The zone's name at that moment — `BST`, `PST`, `CEST` — or its offset where no locale names it.
 *
 * ⚑ `namedAbbreviation` from `utils/timeZones.ts`, which asks en-US and en-GB because each names only
 * its own region's zones (ORB-872). Taken at the moment, because it moves with daylight saving: the
 * same London event reads BST in October and GMT in November.
 */
function zoneLabel(at: Date, zone: string): string {
  return namedAbbreviation(zone, at) || shortOffset(zone, at)
}

/**
 * The moment an online event's start or end names.
 *
 * ⚑⚑ Not a UTC instant, whatever its offset says. The API sends an event's times as the event's own
 * wall clock labelled `+00:00` (`EventTimeFormatter.ToEventZonedWallClock`, ORB-446), and
 * `eventZoneInstant` resolves that clock in the zone that owns it. Reading the offset literally put a
 * London 14:00 at 15:00 BST and flipped a Los Angeles webinar to "run" seven hours before it began.
 * With a blank or unknown zone the API sends the clock that was entered, stored as UTC, which the
 * fallback reads as it is.
 */
function eventMoment(value: string | null | undefined, timeZone: string | undefined): Date | null {
  return value ? eventZoneInstant(value, timeZone) : null
}

/** The calendar day a moment falls on in a zone, as a UTC midnight, so two days can be subtracted. */
function zoneDay(at: Date, zone: string | undefined): number {
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone: zone ?? 'UTC', year: 'numeric', month: '2-digit', day: '2-digit' })
    .formatToParts(at)
  const part = (type: string) => Number(parts.find(entry => entry.type === type)?.value)
  return Date.UTC(part('year'), part('month') - 1, part('day'))
}

/**
 * The event's zone if `Intl` knows it, or undefined.
 *
 * ⚑ An unknown zone name throws `RangeError` from `Intl`, so it is dropped here rather than taking
 * the page down. Without one the API sends the clock that was entered, labelled `+00:00` — a draft
 * with no zone yet, or an import whose zone did not map — and the legacy header and the emails print
 * that clock as it is. So it renders in UTC, which is the clock as sent, and goes unlabelled because
 * nothing says which zone it was. Rendering it in the reader's zone moved it by their offset.
 */
function knownZone(timeZone: string | null | undefined): string | undefined {
  if (!timeZone) return undefined
  try {
    new Intl.DateTimeFormat('en-GB', { timeZone })
    return timeZone
  } catch {
    return undefined
  }
}

/**
 * An online event's start, in the event's own time zone, as `14 Oct 2026, 14:00 BST`.
 *
 * ⚑ Not the reader's zone. The line names the event's zone, so rendering the clock time in the
 * browser's zone beside it would state one time and name another. With no usable zone, the clock as
 * it was entered, unlabelled — see `knownZone`.
 */
export function eventStart(value: string | null | undefined, timeZone: string | null | undefined): string | null {
  const zone = knownZone(timeZone)
  const start = eventMoment(value, zone)
  if (!start) return null
  const when = start.toLocaleString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: zone ?? 'UTC',
  })
  return zone ? `${when} ${zoneLabel(start, zone)}` : when
}

/**
 * An online event's span, in its own zone, as `14 Oct, 14:00 to 15:00 BST`.
 *
 * The date is said once when both ends fall on the same day there, and twice when they do not. With
 * no end, the start alone. Null when there is no start.
 */
export function eventSpan(
  startValue: string | null | undefined,
  endValue: string | null | undefined,
  timeZone: string | null | undefined,
): string | null {
  const zone = knownZone(timeZone)
  const start = eventMoment(startValue, zone)
  if (!start) return null
  const end = eventMoment(endValue, zone)
  const day = (at: Date) => at.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: zone ?? 'UTC' })
  const time = (at: Date) => at.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: zone ?? 'UTC' })
  const tail = zone ? ` ${zoneLabel(start, zone)}` : ''
  if (!end || end.getTime() <= start.getTime()) return `${day(start)}, ${time(start)}${tail}`
  if (day(start) === day(end)) return `${day(start)}, ${time(start)} to ${time(end)}${tail}`
  return `${day(start)}, ${time(start)} to ${day(end)}, ${time(end)}${tail}`
}

/** Where an online event stands against the clock. `days` counts calendar days in the event's zone. */
export type EventClock = { phase: 'ahead'; days: number } | { phase: 'live' } | { phase: 'run' }

/**
 * Where an online event stands against the clock: still to come, under way, or run.
 *
 * ⚑ Run is the end passing, or the start when there is no end. An end that is not after the start is
 * read as no end, the same call `eventSpan` makes, so the two never disagree about bad data.
 *
 * ⚑ The days to go are calendar days in the event's zone, not elapsed time over 24 hours: an event at
 * 09:00 tomorrow is "1 day to go" at 22:00 tonight, and one at 23:00 today "starts today" at 10:00.
 */
export function eventClock(
  startValue: string | null | undefined,
  endValue: string | null | undefined,
  timeZone: string | null | undefined,
  now: Date = new Date(),
): EventClock | null {
  const zone = knownZone(timeZone)
  const start = eventMoment(startValue, zone)
  if (!start) return null
  const endAt = eventMoment(endValue, zone)
  const end = endAt && endAt.getTime() > start.getTime() ? endAt : start
  if (now.getTime() >= end.getTime()) return { phase: 'run' }
  if (now.getTime() >= start.getTime()) return { phase: 'live' }
  return { phase: 'ahead', days: Math.max(0, Math.round((zoneDay(start, zone) - zoneDay(now, zone)) / 86_400_000)) }
}

/** Calendar days from the reader's today to a calendar date: 0 on the day, negative once it has passed. Null if absent. */
export function calendarDaysUntil(value: string | null | undefined, now: Date = new Date()): number | null {
  const parts = calendarParts(value)
  if (!parts) return null
  const date = Date.UTC(parts.year, parts.month - 1, parts.day)
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate())
  return Math.round((date - today) / 86_400_000)
}
